import {EventPayloads, EventType} from "./IntegrationEventTypes.ts";

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null && !Array.isArray(value);
};

const hasString = (value: unknown, key: string): boolean => {
    return isRecord(value) && typeof value[key] === "string";
};

const hasBoolean = (value: unknown, key: string): boolean => {
    return isRecord(value) && typeof value[key] === "boolean";
};

const hasNumber = (value: unknown, key: string): boolean => {
    return isRecord(value) && typeof value[key] === "number" && Number.isFinite(value[key]);
};

const optionalString = (value: Record<string, unknown>, key: string): boolean => {
    return value[key] === undefined || typeof value[key] === "string";
};

const isDeviceStatus = (value: unknown): boolean => {
    return value === "online" || value === "offline";
};

const isTimeOfDay = (value: unknown): boolean => {
    return hasNumber(value, "hour") && hasNumber(value, "minute") && hasNumber(value, "second");
};

export const isPayloadForType = <T extends EventType>(
    type: T,
    payload: unknown
): payload is EventPayloads[T] => {
    switch (type) {
        case EventType.Ready:
            return isRecord(payload)
                && (payload.version === undefined || typeof payload.version === "string");
        case EventType.ReadyAck:
            return isRecord(payload)
                && (payload.frontendVersion === undefined || typeof payload.frontendVersion === "string")
                && (payload.backendVersion === undefined || typeof payload.backendVersion === "string");
        case EventType.DeviceConnected:
        case EventType.DeviceDisconnected:
            return hasString(payload, "deviceId");
        case EventType.RequestStatus:
        case EventType.RebootRequest:
        case EventType.ScreenshotRequest:
            return hasString(payload, "requestId") && hasString(payload, "deviceId");
        case EventType.StatusResponse:
            return hasString(payload, "requestId")
                && hasString(payload, "deviceId")
                && isRecord(payload)
                && isDeviceStatus(payload.status);
        case EventType.RebootResponse:
            return hasString(payload, "requestId")
                && hasString(payload, "deviceId")
                && hasBoolean(payload, "success");
        case EventType.ScreenshotResponse:
            return hasString(payload, "requestId")
                && hasString(payload, "deviceId");
        case EventType.ScreenshotPolicy:
            return hasNumber(payload, "interval")
                && hasNumber(payload, "retention")
                && isRecord(payload)
                && isTimeOfDay(payload.offloadTimestamp);
        case EventType.AuditSend:
            return hasString(payload, "requestId") && hasString(payload, "deviceId");
        case EventType.AuditResponse:
            return hasString(payload, "requestId")
                && hasString(payload, "deviceId")
                && hasBoolean(payload, "success")
                && hasNumber(payload, "uploaded")
                && isRecord(payload)
                && optionalString(payload, "error");
        case EventType.MonitorRequest:
            return hasString(payload, "requestId")
                && hasString(payload, "deviceId")
                && hasString(payload, "cmd")
                && hasString(payload, "data");
        case EventType.MonitorResponse:
            return hasString(payload, "requestId")
                && hasString(payload, "deviceId")
                && isRecord(payload)
                && optionalString(payload, "data");
        case EventType.MonitorStatus:
            if (!isRecord(payload)) return false;
            if (!hasString(payload, "manufacturer")) return false;
            if (!hasNumber(payload, "productCode")) return false;
            if (!hasString(payload, "profile")) return false;
            if (payload.backlight !== undefined && typeof payload.backlight !== "number") return false;
            return optionalString(payload, "power")
                && optionalString(payload, "source")
                && optionalString(payload, "error");
        case EventType.Error:
            if (!isRecord(payload)) return false;
            if (payload.requestId !== undefined && typeof payload.requestId !== "string") return false;
            return typeof payload.code === "string" && typeof payload.message === "string";
        case EventType.UpdaterManifestUrlRequest:
            return hasString(payload, "requestId");
        case EventType.UpdaterManifestUrlResponse:
            if (!isRecord(payload)) return false;
            if (payload.requestId !== undefined && typeof payload.requestId !== "string") return false;
            return payload.manifestUrl === null || typeof payload.manifestUrl === "string";
        default:
            return false;
    }
};
